import { useMemo } from "react";
import { IActionCategory, IComponentType, IComponentUnion, IComponentsById, IPlaySoundAction, ISceneComp, ISpatialComponentUnion } from "@r3f-component-data-structure";
import React from "react";
import { isAbstractComponent, toRadians } from "@r3f/utils/general";
import { Button, Image, Text, Text3d, Video, Model3d } from '@r3f/index';
import { filestore, FsImage, FsModel3D, FsVideo } from "@/filestore";
import { resolveAssetUrl } from "@/asset-urls";
import { EntityErrorBoundary } from "@r3f/components/boundaries";
import { presetAnimationController } from "@/components/PresetAnimationController";
import { useGetCurrentSceneId } from "@/hooks/general";
import { IEntityActionTypes, ISceneActions, ITransCatTypes, useActionStateStore, useDataStore, useSceneStore } from "@/zustand";
import { useThree } from "@react-three/fiber";
import { getAllAnimationControllerOptions } from "@/utils/action";
import { getAllOnloadModelAnimationPlayDataBySceneId } from "@r3f/utils/actions";

export const getPlacementSceneContent = (sceneId: string, componentsById: IComponentsById | null, enableBVH: boolean = true) => {
    if (!sceneId || !componentsById) return;
    const scene = componentsById?.[sceneId] as ISceneComp;
    return scene.children.map((id, index) => {
        let component = componentsById?.[id] as IComponentUnion;

        if (isAbstractComponent(component)) return null;
        if (component.isHidden) return null; // Hidden entities never show in placement mode
         let entity = component as ISpatialComponentUnion;

        const renderOrder = index + 1;
        const props = { id, renderOrder: renderOrder * 10, sceneId };

        let { position, rotation, actions, transitions, ...restProps } = entity as any;

        position = [entity.position[0] / 2, entity.position[1] / 2, entity.position[2] / 2];

        rotation = [toRadians(entity.rotation[0]), toRadians(entity.rotation[1]), toRadians(entity.rotation[2])];

        const spatialProps: any = { rotation, position };
        if (restProps.fontSize) spatialProps.fontSize = restProps.fontSize / 2;

        // These components are NOT the HOC-wrapped entities, so there is no per-entity boundary here —
        // and this runs inside PlacementScene's useMemo, where a throw would escape to the canvas-wide
        // boundary and take the camera down with it. Hence resolve-then-placeholder rather than a catch.
        const brokenEntity = (
            <EntityErrorBoundary
                key={id}
                forceError
                renderOrder={props.renderOrder}
                scale={restProps.scale ?? [1, 1, 1]}
                rotation={rotation}
                position={position}
            />
        );

        switch (entity.type) {
            case IComponentType.Button:
                return <Button key={id} {...restProps} {...(spatialProps ?? {})} {...props} nonTransientScale={restProps.scale} opacity={0.2} animationTransformGroupPrefix='placement-'/>;
            case IComponentType.Image:
                restProps.imageUrl = resolveAssetUrl('placement image', restProps.filestoreId, (fsId) => filestore.get<FsImage>(fsId).getImageUrl());
                if (!restProps.imageUrl) return brokenEntity;
                return <Image key={id} {...restProps} {...spatialProps} {...props} animationTransformGroupPrefix='placement-'/>;
            case IComponentType.Text:
                return <Text key={id} {...restProps} {...spatialProps} {...props} animationTransformGroupPrefix='placement-'/>;
            case IComponentType.Text3d:
                return <Text3d key={id} {...restProps} {...spatialProps} {...props} animationTransformGroupPrefix='placement-'/>;
            case IComponentType.Video:
                // The playback url decides: `useHlsLoader` assumes a string, while the thumbnail is
                // already undefined-tolerant downstream.
                restProps.videoUrl = resolveAssetUrl('placement video', restProps.filestoreId, (fsId) => filestore.get<FsVideo>(fsId).getPlaybackUrl());
                if (!restProps.videoUrl) return brokenEntity;
                restProps.thumbnailUrl = resolveAssetUrl('placement video poster', restProps.filestoreId, (fsId) => filestore.get<FsVideo>(fsId).getThumbnailUrl());
                return <Video key={id} {...restProps} {...spatialProps} {...props} enabled={false} thumbnailOnly animationTransformGroupPrefix='placement-'/>;
            case IComponentType.Model3d:
                restProps.model3dUrl = resolveAssetUrl('placement model', restProps.filestoreId, (fsId) => filestore.get<FsModel3D>(fsId).getModelUrl());
                if (!restProps.model3dUrl) return brokenEntity;
                return <Model3d key={id} {...restProps} {...spatialProps} {...props} enableBVH={enableBVH} animationTransformGroupPrefix='placement-'/>;
            default:
                return null;
        }
    });
}


export const useGetPlacementSceneContent = (sceneId: string, componentsById: IComponentsById | null, enableBVH: boolean = true) => {
    return useMemo(() => getPlacementSceneContent(sceneId, componentsById, enableBVH), [sceneId, componentsById]);
}

export const usePlacementBehaviour = () => {
    const { scene } = useThree(); 
    const currentSceneId = useGetCurrentSceneId();
    const sceneDispatch = useSceneStore(({ dispatch }) => dispatch);
	const entityActionDispatch = useActionStateStore(({ dispatch }) => dispatch);
    const componentsById = useDataStore((state) => state.componentsById);
    const sceneComp = useMemo(() => componentsById?.[currentSceneId], [currentSceneId, componentsById]) as ISceneComp;
    const sceneOnLoadPlaySoundActions = useMemo(() => sceneComp?.actions?.onLoad?.filter(action => action.type === IActionCategory.playSound), [sceneComp]) as IPlaySoundAction[];

    const enterPlacementBehaviour = () => {
        presetAnimationController.resetAllAnimationsBySceneId(currentSceneId);
        entityActionDispatch?.({
            type: IEntityActionTypes.PAUSE_ALL_MODEL_ANIMATIONS
        });
        // hides prompt if placement mode set to false
        sceneDispatch?.({
            type: ISceneActions.SET_PLACEMENT_MODE,
            placementMode: true,
        });

        // // Reset scale of group
        // setGroupScale([1, 1, 1]);
        // Reset camera position offset
        // placementCameraOffsetRef.current = initialPlacementCameraOffset;

        // Show any entities that were hidden by a toggleVisibility action
        Object.entries(componentsById ?? {}).forEach(([id, component]) => {
            if(isAbstractComponent(component)) return;
            const prospectiveGroupId = `toggleVisibilityGroup-${id}`;
            const threeGroup = scene.getObjectByName(prospectiveGroupId);
            if (threeGroup) {
                threeGroup.visible = !component.isHidden;
            }
        });

    }

    const exitPlacementBehaviour = () => {
        presetAnimationController
            .set({options: getAllAnimationControllerOptions(componentsById!).preset})
            .startAllOnloadAnimationsBySceneId(currentSceneId)

        entityActionDispatch?.({
            type: IEntityActionTypes.PLAY_MODEL_ANIMATIONS_BY_ID,
            payload: getAllOnloadModelAnimationPlayDataBySceneId(componentsById, currentSceneId)
        });

        sceneOnLoadPlaySoundActions?.forEach((action) => {
            let { stopMedia } = action;
            if (stopMedia) {
                entityActionDispatch?.({
                    type: IEntityActionTypes.PAUSE_ALL_VIDEOS,
                });
                entityActionDispatch?.({
                    type: IEntityActionTypes.PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID,
                    payload: { id: currentSceneId },
                });
                entityActionDispatch?.({
                    type: IEntityActionTypes.PAUSE_BACKGROUND_AUDIO,
                });
            }
            entityActionDispatch?.({
                type: IEntityActionTypes.PLAY_ALL_SCENE_AUDIO_BY_SCENE_AND_ENTITY_ID,
                payload: { sceneId: currentSceneId, entityIds: [currentSceneId] },
            });	
        })

        // start enter transition for current scene once scene has been placed
        sceneDispatch?.({
            type: ISceneActions.SET_TRANSITION_CATEGORY,
            transCatDict: {
                [currentSceneId]: ITransCatTypes.enter,
            },
        });
        // hides prompt if placement mode set to false
        sceneDispatch?.({
            type: ISceneActions.SET_PLACEMENT_MODE,
            placementMode: false,
        });
    }

    return {enterPlacementBehaviour, exitPlacementBehaviour}
}

